/**
 * Teams for the request-type builder (RESTORE_PLAN R1.3): teams are names on a company's spaces
 * (`jira_space:{companyId}:{spaceId}`.teams). The builder lists them and can add one inline.
 */
import { projectId } from './supabase/info';
import { collectTeams, spaceForNewTeam, type SpaceWithTeams } from './requestTypeAccess';

const BASE = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd`;

export async function listCompanySpaces(companyId: string, accessToken: string): Promise<SpaceWithTeams[]> {
  const res = await fetch(`${BASE}/spaces/${encodeURIComponent(companyId)}`, { headers: { Authorization: `Bearer ${accessToken}` } });
  if (!res.ok) throw new Error(`Could not load the company's spaces (${res.status})`);
  const data = await res.json();
  const spaces = Array.isArray(data) ? data : data?.spaces || [];
  return spaces as SpaceWithTeams[];
}

export async function listCompanyTeams(companyId: string, accessToken: string): Promise<{ teams: string[]; spaces: SpaceWithTeams[] }> {
  const spaces = await listCompanySpaces(companyId, accessToken);
  return { teams: collectTeams(spaces), spaces };
}

/**
 * Add a team by name. With a space to hold it, the name is written onto that space (the first active
 * JSM space unless one is given); with no space yet, the name lives on the request type alone and
 * the caller is told so.
 */
export async function addTeam(
  companyId: string,
  name: string,
  accessToken: string,
  spaces: SpaceWithTeams[],
  spaceId?: string,
): Promise<{ name: string; savedToSpace: SpaceWithTeams | null }> {
  const team = name.trim();
  if (!team) throw new Error('A team needs a name');
  const target = (spaceId ? spaces.find((s) => s.id === spaceId) : null) || spaceForNewTeam(spaces);
  if (!target) return { name: team, savedToSpace: null };
  const existing = target.teams || [];
  if (existing.some((t) => t.toLowerCase() === team.toLowerCase())) return { name: team, savedToSpace: target };
  const res = await fetch(`${BASE}/spaces/${encodeURIComponent(companyId)}/${encodeURIComponent(target.id)}`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ teams: [...existing, team] }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Could not add the team (${res.status})`);
  }
  target.teams = [...existing, team];
  return { name: team, savedToSpace: target };
}
